/**
 * Secure Registration Storage Utility
 * 
 * Stores registration form data securely using:
 * - sessionStorage (cleared when browser tab closes)
 * - AES-GCM encryption via Web Crypto API
 * - Automatic data expiration
 * 
 * Note: Client-side storage is never 100% secure, but this provides
 * the best available protection for sensitive registration data.
 */

import type { IdentityProvider } from '../config/msal.config';

const STORAGE_KEY = 'reg_data';
const STORAGE_EXPIRY_KEY = 'reg_data_expiry';
const EXPIRY_DURATION_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Derive a key from a password using PBKDF2
 * This creates a consistent key from a session-based password
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: 100000,
      hash: 'SHA-256' as AlgorithmIdentifier,
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Generate a session-based password from browser fingerprint
 * This makes the encryption key unique per session
 */
function getSessionPassword(): string {
  // Use a combination of sessionStorage ID and timestamp
  // This creates a unique key per browser session
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
}

/**
 * Encrypt data using AES-GCM
 */
async function encryptData(data: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const dataBytes = encoder.encode(data);

    // Generate salt for key derivation
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM

    // Derive key from session password
    const sessionPassword = getSessionPassword();
    const key = await deriveKey(sessionPassword, salt);

    // Encrypt the data
    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      dataBytes
    );

    // Combine salt, IV, and encrypted data
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);

    // Convert to base64 for storage
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt registration data');
  }
}

/**
 * Decrypt data using AES-GCM
 */
async function decryptData(encryptedData: string): Promise<string> {
  try {
    // Convert from base64
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

    // Extract salt, IV, and encrypted data
    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const encrypted = combined.slice(28);

    // Derive key from session password
    const sessionPassword = getSessionPassword();
    const key = await deriveKey(sessionPassword, salt);

    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
      },
      key,
      encrypted
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt registration data');
  }
}

/**
 * Check if stored data has expired
 */
function isExpired(): boolean {
  try {
    const expiry = sessionStorage.getItem(STORAGE_EXPIRY_KEY);
    if (!expiry) return true;
    return Date.now() > parseInt(expiry, 10);
  } catch {
    return true;
  }
}

/**
 * Set expiry timestamp
 */
function setExpiry(): void {
  try {
    const expiry = Date.now() + EXPIRY_DURATION_MS;
    sessionStorage.setItem(STORAGE_EXPIRY_KEY, expiry.toString());
  } catch (error) {
    console.error('Failed to set expiry:', error);
  }
}

export interface ProviderLicenseData {
  licenseNumber: string;
  licenseState: string;
  licenseExpirationDate: string;
  deaNumber: string;
  deaExpirationDate: string;
  boardCertification: string;
  certificationExpirationDate: string;
  specialties: string[];
  specialtiesCode?: string[];
  additionalCredentials: string;
}

export interface RegistrationData {
  npiNumber?: string;
  roleName?: string;
  /** RBAC role code (e.g. Provider, OfficeAdmin) – sent in registration API. */
  roleCode?: string;
  /** RBAC role id – optional for APIs that accept role id. */
  roleId?: number;
  npiValidationData?: {
    isValid: boolean;
    npiNumber?: string;
    message?: string;
    providerInfo?: {
      enumerationType?: string;
      basic?: {
        name?: string;
        credential?: string;
        firstName?: string;
        lastName?: string;
        middleName?: string;
        organizationName?: string;
      };
      addresses?: Array<{
        addressPurpose?: string;
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        countryCode?: string;
        telephoneNumber?: string;
        faxNumber?: string;
      }>;
      taxonomies?: Array<{
        code?: string;
        description?: string;
        isPrimary?: boolean;
      }>;
    };
  };
  demographics?: {
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    gender: string;
    otherGender: string;
    dateOfBirth: string;
    email: string;
    confirmEmail: string;
    // Support both new phoneNumbers structure and legacy phoneNumber/alternatePhone
    phoneNumbers?: {
      mobile: { phoneType: string; countryCode: string; phoneNumber: string; isPrimary: boolean; extension?: string };
      home: { phoneType: string; countryCode: string; phoneNumber: string; isPrimary: boolean; extension?: string };
      office: { phoneType: string; countryCode: string; phoneNumber: string; isPrimary: boolean; extension?: string };
      emergency: { phoneType: string; countryCode: string; phoneNumber: string; isPrimary: boolean; extension?: string };
    };
    phoneNumber?: string; // Legacy format
    alternatePhone?: string; // Legacy format
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
  };
  providerLicense?: ProviderLicenseData;
  accountSetupMethod?: string;
  idp?: IdentityProvider;
  accountData?: unknown;
  // New fields for users coming from login (Google/Microsoft authentication)
  authenticatedUser?: {
    email: string;
    name: string;
    entraObjectId: string;
    accessToken: string;
  };
  isNewUserFromLogin?: boolean;
  /** Set when Office Admin / Pre-Exam Preparer verify their provider (GET /api/v1/providers/validate). */
  providerValidationData?: {
    clinicianProfileId: number;
    providerFullName?: string | null;
    userId?: number;
  };
}

/**
 * Store registration data securely
 */
export async function storeRegistrationData(data: RegistrationData): Promise<boolean> {
  try {
    // Ensure npiValidationData is stored as an object, not a string
    const dataToStore: RegistrationData = { ...data };
    
    // Debug: Log what we're receiving
    if (import.meta.env.DEV && dataToStore.npiValidationData) {
      console.log('Storage - Storing npiValidationData:', {
        type: typeof dataToStore.npiValidationData,
        hasProviderInfo: !!(dataToStore.npiValidationData as any)?.providerInfo,
        isValid: (dataToStore.npiValidationData as any)?.isValid,
        structure: dataToStore.npiValidationData
      });
    }
    
    if (dataToStore.npiValidationData && typeof dataToStore.npiValidationData === 'string') {
      try {
        dataToStore.npiValidationData = JSON.parse(dataToStore.npiValidationData);
        if (import.meta.env.DEV) {
          console.log('Storage - Parsed npiValidationData from string before storing');
        }
      } catch (error) {
        console.warn('Storage - npiValidationData is already a string but failed to parse:', error);
        // If it's already a string and can't be parsed, it might be invalid JSON
        // Keep it as is, but log a warning
      }
    }
    
    // Debug: Log what we're storing
    if (import.meta.env.DEV && dataToStore.npiValidationData) {
      console.log('Storage - Final npiValidationData to store:', {
        type: typeof dataToStore.npiValidationData,
        hasProviderInfo: !!(dataToStore.npiValidationData as any)?.providerInfo,
        isValid: (dataToStore.npiValidationData as any)?.isValid
      });
    }

    // Check if Web Crypto API is available and we're in a secure context
    const isSecureContext = typeof window !== 'undefined' && window.isSecureContext;
    const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
    
    if (!isSecureContext || !hasCrypto) {
      // Only warn in development or if not in a secure context
      if (import.meta.env.DEV || !isSecureContext) {
        console.warn('Web Crypto API not available, falling back to unencrypted storage. For encryption, use HTTPS or localhost.');
      }
      // Fallback to unencrypted sessionStorage (still better than localStorage)
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
      setExpiry();
      return true;
    }

    const jsonData = JSON.stringify(dataToStore);
    const encrypted = await encryptData(jsonData);
    sessionStorage.setItem(STORAGE_KEY, encrypted);
    setExpiry();
    return true;
  } catch (error) {
    console.error('Failed to store registration data:', error);
    return false;
  }
}

/**
 * Retrieve registration data securely
 */
export async function getRegistrationData(): Promise<RegistrationData | null> {
  try {
    // Check if data exists and hasn't expired
    if (isExpired()) {
      clearRegistrationData();
      return null;
    }

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    // Check if Web Crypto API is available and we're in a secure context
    const isSecureContext = typeof window !== 'undefined' && window.isSecureContext;
    const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
    
    let parsedData: RegistrationData;
    
    if (!isSecureContext || !hasCrypto) {
      // Fallback: try to parse as unencrypted JSON
      try {
        parsedData = JSON.parse(stored) as RegistrationData;
      } catch {
        return null;
      }
    } else {
      const decrypted = await decryptData(stored);
      parsedData = JSON.parse(decrypted) as RegistrationData;
    }

    // Ensure npiValidationData is parsed as JSON object if it's stored as a string
    if (parsedData.npiValidationData) {
      if (typeof parsedData.npiValidationData === 'string') {
        try {
          parsedData.npiValidationData = JSON.parse(parsedData.npiValidationData);
          if (import.meta.env.DEV) {
            console.log('Storage - Parsed npiValidationData from string to object');
          }
        } catch (error) {
          console.warn('Storage - Failed to parse npiValidationData from string:', error);
          // Keep it as string if parsing fails
        }
      }
      
      // Debug: Log the structure of npiValidationData
      if (import.meta.env.DEV) {
        console.log('Storage - Retrieved npiValidationData:', {
          type: typeof parsedData.npiValidationData,
          hasProviderInfo: !!(parsedData.npiValidationData as any)?.providerInfo,
          isValid: (parsedData.npiValidationData as any)?.isValid,
          structure: parsedData.npiValidationData
        });
      }
    }

    return parsedData;
  } catch (error) {
    console.error('Failed to retrieve registration data:', error);
    // Clear corrupted data
    clearRegistrationData();
    return null;
  }
}

/**
 * Clear all registration data
 */
export function clearRegistrationData(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_EXPIRY_KEY);
    // Note: We keep session_id to maintain encryption consistency
  } catch (error) {
    console.error('Failed to clear registration data:', error);
  }
}

/**
 * Check if registration data exists
 */
export function hasRegistrationData(): boolean {
  try {
    if (isExpired()) {
      clearRegistrationData();
      return false;
    }
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

